import React, { useEffect } from 'react'
import { BreakpointProvider } from 'react-socks'
import { useDispatch } from 'react-redux'
import 'lazysizes'
import { useStaticQuery, graphql } from 'gatsby'

import Header from '~/components/features/header/header'
import Footer from '~/components/core/footer'
import AlertPopin from '~/components/core/alertPopin'

import scrollManager from '~/utils/scrollManager'

import '../../styles/styles.scss'

import {
  changeLang,
  setLaboEmptyLabo,
  setLaboEmptyOther,
  setCurrentMainTag,
  setCurrentprofile,
} from '~/store/actions'
import {waitForElm} from "~/utils/wait_for_element";

interface LayoutProps {
  initLocale: string
  frUrl: string
  enUrl: string
  headerWhite?: boolean
  children: React.ReactNode
  needReloading?: boolean
  isPresentation?: boolean
  noBg?: boolean
  profile?: any
  mainTag?: string
  footerBorder?: boolean
  isSimulator?: boolean | undefined
}

export const Layout = ({
  initLocale,
  frUrl,
  enUrl,
  headerWhite = false,
  children,
  needReloading = false,
  isPresentation = false,
  noBg = false,
  profile = false,
  mainTag = '',
  footerBorder = false,
    isSimulator = false,
}: LayoutProps) => {
  const dispatch = useDispatch()
  useEffect(() => {
    dispatch(changeLang(initLocale))
  }, [initLocale])

  useEffect(() => {
    waitForElm('#onetrust-pc-btn-handler').then((els) => {
      els.forEach(el => el.focus())
    })
  }, []);

  const datas: {
    learningSpace_fr: Queries.ContentfulLinkPage,
    learningSpace_en: Queries.ContentfulLinkPage,
    labo: Queries.ContentfulLaboGroupConnection
    news: Queries.ContentfulNewsGroupConnection
    events: Queries.ContentfulEventGroupConnection
    pages: Queries.ContentfulPageGroupConnection
    laboPopin: Queries.ContentfulLaboPopinGroupConnection
  } = useStaticQuery(graphql`
    query {
      learningSpace_fr: contentfulLinkPage(contentful_id: { eq: "4FlzfPQoFocf0XJ1DlTo2Y" }, node_locale: { eq: "fr" }) {
        label,
        pageLink {
          ...ContentfulHubFields
        },
        node_locale
      }
      learningSpace_en: contentfulLinkPage(contentful_id: { eq: "4FlzfPQoFocf0XJ1DlTo2Y" }, node_locale: { eq: "en" }) {
        label,
        pageLink {
          ...ContentfulHubFields
        },
        node_locale
      }
      labo: allContentfulLabo {
        nodes {
          distributionChannel {
            title
          }
          title
          node_locale
          mainTag {
            icon
            title
          }
        }
      }
      news: allContentfulNews {
        nodes {
          distributionChannel {
            title
          }
          title
          publicationDate
          node_locale
          createdAt
          publicationDate
          mainTag {
            title
          }
        }
      }
      events: allContentfulEvent {
        nodes {
          distributionChannel {
            title
          }
          title
          node_locale
          startDate
          mainTag {
            title
          }
        }
      }
      pages: allContentfulPage {
        nodes {
          distributionChannel {
            title
          }
          title
          slug
          node_locale
          mainTag {
            title
          }
        }
      }
      laboPopin: allContentfulLaboPopin {
        nodes {
          node_locale
          emptyTag {
            title
          }
          distributionChannel {
            title
          }
          emptyLabo {
            title
            node_locale
            mainTag {
              icon
            }
          }
          emptyNews {
            title
            node_locale
            createdAt
            publicationDate
          }
          emptyEvents {
            title
            node_locale
            startDate
          }
          emptyPages {
            title
            slug
            node_locale
          }
        }
      }
    }
  `)

  const learningSpace = {
    fr: datas.learningSpace_fr,
    en: datas.learningSpace_en,
  }

  const getItems = (
    items:
      | Queries.ContentfulNews[]
      | Queries.ContentfulEvent[]
      | Queries.ContentfulPage[]
      | Queries.ContentfulLabo[]
      | null,
    emptyTag: any,
  ) => {
    let emptyEls =
      items?.filter(
        (news) =>
          news.node_locale === initLocale &&
          news.title !== null &&
          news.title !== 'Dummy content' &&
          news.mainTag?.title === emptyTag,
      ) || []

    emptyEls = emptyEls.filter((news) => {
      let isIn = false
      news?.distributionChannel?.forEach((channel) => {
        if (channel?.title === 'Site internet') {
          isIn = true
        }
      })
      return isIn
    })

    if (emptyEls.length > 3) {
      emptyEls = emptyEls.slice(0, 3)
    }

    return emptyEls
  }

  setTimeout(() => {
    datas.laboPopin.nodes.forEach((item) => {
      if (
        item.node_locale === initLocale &&
        item?.distributionChannel &&
        item?.distributionChannel?.filter(
          (channel) => channel?.title === 'Site internet',
        ).length > 0
      ) {
        let emptyLabo =
          item.emptyLabo !== null
            ? item.emptyLabo
            : getItems(Array.from(datas.labo.nodes), item?.emptyTag?.title)
        dispatch(setLaboEmptyLabo(emptyLabo))

        const emptyOther = {
          news:
            item.emptyNews ??
            getItems(Array.from(datas.news.nodes), item?.emptyTag?.title),
          events:
            item.emptyEvents !== null
              ? item.emptyEvents
              : getItems(Array.from(datas.events.nodes), item?.emptyTag?.title),
          pages:
            item.emptyPages !== null
              ? item.emptyPages
              : getItems(Array.from(datas.pages.nodes), item?.emptyTag?.title),
        }

        if (emptyOther.news !== null) {
          dispatch(setLaboEmptyOther(emptyOther))
        }
      }
    })

    // case multiple / single profile
    if (profile !== null) dispatch(setCurrentprofile(profile))
    dispatch(setCurrentMainTag(mainTag))
  }, 0)

  useEffect(() => {
    scrollManager.restart()
    sessionStorage.setItem(
      'prevUrl',
      sessionStorage.getItem('currentUrl') ?? '',
    )
    sessionStorage.setItem('currentUrl', window.location.href)
  }, [])

  return (
    <div className={isSimulator ? "relative w-full" : "app"}>
      {!isPresentation && (
        <Header
          frUrl={frUrl}
          enUrl={enUrl}
          noBg={noBg}
          needReloading={needReloading}
          headerWhite={headerWhite}
          learningSpace={learningSpace}
        />
      )}
      <div className="app-inner flex">
        <BreakpointProvider>
          <div className="flex w-full">{children}</div>
        </BreakpointProvider>
      </div>

      <AlertPopin />

      {!isPresentation && <Footer hasBorder={footerBorder} />}
    </div>
  )
}